<template>
    <b-modal
      id="DDTStatsModal"
      title="Statistic Report"
      content-class="modal-content-full"
      dialog-class="modal-dialog-full"
      modal-header-class="ptv-modal-header"
      size="xl"
      centered
      scrollable
      @hidden="reset()"
      @show="generateStatistics()"
    >
        <!-- Modal Title -->
        <template #modal-header>
            <div class="d-flex align-items-center justify-content-between w-100">
                <h5 class="mb-0 font-weight-semibold text-white">Statistic Report</h5>
                <span v-if="!dataLoading" class="download-button-container" @click="downloadReports">        
                    <fa :icon="['fas', 'download']" size="lg"/>
                </span>
            </div>
        </template>

        <div class="ddt-form-area">
            <div class="ddt-form-row">
                <div class="ddt-field ddt-field--type">
                    <label class="ddt-form-label">Delivery Type</label>
                    <b-form-select v-model="deliveryType">
                        <template #first>
                            <b-form-select-option :value="null" disabled>Select Type</b-form-select-option>
                        </template>
                        <b-form-select-option value="INPX">INPX</b-form-select-option>
                    </b-form-select>
                </div>
                <div class="ddt-field ddt-field--date">
                    <label class="ddt-form-label text-nowrap">Requested Delivery Date</label>
                    <b-form-input
                        type="date"
                        placeholder="Select date"
                        format="yyyy/mm/dd"
                        v-model="deliveryDate"
                    ></b-form-input>
                </div>
                <div class="ddt-field ddt-field--notes">
                    <label class="ddt-form-label">Notes</label>
                    <b-form-textarea v-model="note" rows="1" max-rows="3"></b-form-textarea>
                </div>
            </div>
        </div>

        <div class="stats-dash" v-if="statsObjs.length">
            <!-- Section title + loading indicator -->
            <div class="stats-dash__head">
                <span class="ddt-section-title">Statistical Analysis</span>
                <transition name="fade">
                    <span v-if="isCellsLoading" class="stats-loading-banner d-flex align-items-center">
                        <span class="spinner-border spinner-border-sm loading-banner-spinner mr-2" role="status"></span>
                        <span class="loading-banner-text">Statistics are loading…</span>
                    </span>
                </transition>
            </div>

            <!-- Toolbar: sample tabs (multi) + single request action -->
            <div class="stats-toolbar" v-if="statsObjs.length > 1 || showRequestBtn">
                <div class="stats-samples" v-if="statsObjs.length > 1">
                    <button
                        v-for="(statsObj, index) in statsObjs"
                        :key="'tab-' + index"
                        class="stats-sample-tab"
                        :class="{ 'stats-sample-tab--active': activeSample === index }"
                        @click="activeSample = index"
                    >Sample {{ index + 1 }}</button>
                </div>
                <b-button
                    v-if="showRequestBtn"
                    :disabled="dataLoading"
                    variant="none"
                    class="request-btn stats-toolbar__request"
                    @click="openConfirmation(statsObjs[activeSample], activeSample)"
                >
                    <fa :icon="['fas', 'paper-plane']" class="request-btn__icon"/>
                    <span>Request{{ statsObjs.length > 1 ? ' Sample ' + (activeSample + 1) : '' }}</span>
                </b-button>
            </div>

            <!-- Summary strip (full width, horizontal) -->
            <div class="stat-card stat-card--summary stat-summary" v-if="summarySection">
                <div class="stat-card__head">
                    <fa :icon="['fas', summarySection.icon]" class="stat-card__icon"/>
                    <span class="stat-card__title">{{ summarySection.label }}</span>
                </div>
                <div class="stat-summary__row">
                    <div class="stat-summary__item" v-for="metric in summarySection.metrics" :key="metric.key">
                        <span class="stat-summary__label">{{ metric.label }}</span>
                        <span class="stat-summary__value">
                            <span v-if="statsObjs[activeSample] && statsObjs[activeSample][metric.key] !== undefined">{{ statsObjs[activeSample][metric.key] }}</span>
                            <span v-else class="spinner-border spinner-border-sm cell-spinner" role="status"></span>
                        </span>
                    </div>
                </div>
            </div>

            <!-- Section cards laid out as columns -->
            <div class="stats-cards">
                <div class="stat-col" v-for="(column, ci) in statColumns" :key="ci">
                    <div
                        class="stat-card"
                        :class="{ 'stat-card--summary': section.label === 'Summary' }"
                        v-for="section in column"
                        :key="section.label"
                    >
                        <div class="stat-card__head">
                            <fa :icon="['fas', section.icon]" class="stat-card__icon"/>
                            <span class="stat-card__title">{{ section.label }}</span>
                        </div>
                        <div class="stat-card__body">
                            <div class="stat-line" v-for="metric in section.metrics" :key="metric.key">
                                <span class="stat-line__label">{{ metric.label }}</span>
                                <span class="stat-line__value">
                                    <span v-if="statsObjs[activeSample] && statsObjs[activeSample][metric.key] !== undefined">{{ statsObjs[activeSample][metric.key] }}</span>
                                    <span v-else class="spinner-border spinner-border-sm cell-spinner" role="status"></span>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <template #modal-footer>
            <b-row class="w-100 justify-content-end">
                <b-button class="close-btn px-4 py-2" @click="closeStatsModal()">Close</b-button>
            </b-row>
        </template>

        <!-- Confirmation Modal (DDT) -->
        <b-modal
          id="confirmSampleRequest"
          size="md"
          content-class="sub-menu-transparent"
          title="Data Request Confirmation"
        >
            <template #modal-header>
                <div class="d-flex align-items-center justify-content-between w-100">
                    <h5 class="mb-0 font-weight-semibold text-white">Data Request Confirmation</h5>
                </div>
            </template>
            <template #modal-footer>
                <b-row class="w-100 justify-content-end">
                    <b-button class="close-btn px-4 py-2" @click="closeConfirmation()">Close</b-button>
                </b-row>
            </template>
            <b-container>
                <b-row class="py-2 text-center">
                    <b-col>
                        Data request with total segments length of <b class="highlight">{{ requestedSample['All segments length'] }}</b> will be sent to DMP.
                    </b-col>
                </b-row>
                <b-row class="py-2 text-center">
                    <b-col>Please confirm and click submit to send the request.</b-col>
                </b-row>
                <b-row class="py-2 w-100 justify-content-center" no-gutters>
                    <b-button class="confirm-btn px-3 py-2" @click="submitRequest()">Submit</b-button>
                </b-row>
            </b-container>
        </b-modal>
    </b-modal>
</template>

<style>
/* Modal shell */
#DDTStatsModal .modal-content {
    border: none;
    border-radius: 5px;
    overflow: hidden;
    box-shadow: 0 12px 40px rgba(6, 60, 109, 0.18);
}

#DDTStatsModal .modal-header {
    background: #063c6d;
    color: white !important;
    padding: 0.8rem 1.25rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

#confirmSampleRequest .modal-header {
    background: #063c6d;
    color: white !important;
    padding: 0.8rem 1.25rem;
}

#DDTStatsModal .modal-body {
    padding-top: 0 !important;
    /* background: #f0f4f9; */
}

/* Form area — sleek single horizontal row */
#DDTStatsModal .ddt-form-area {
    background: #f7fafd;
    border-bottom: 1px solid #e1eaf3;
    padding: 1rem 1.375rem;
}

#DDTStatsModal .ddt-form-row {
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 1rem;
    width: 100%;
    max-width: 57.5rem;
    margin: 0 auto;
}

#DDTStatsModal .ddt-field {
    display: flex;
    flex-direction: column;
    min-width: 0;
}
#DDTStatsModal .ddt-field--type {
    width: 11.25rem;
    flex: 0 0 11.25rem;
}
#DDTStatsModal .ddt-field--date {
    width: 12.5rem;
    flex: 0 0 12.5rem;
}
#DDTStatsModal .ddt-field--notes {
    flex: 1 1 100%;
    width: 100%;
}

#DDTStatsModal .ddt-form-label {
    display: block;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: #4a6880;
    margin-bottom: 0.3rem;
}

/* Form inputs — consistent rounded fields with a blue focus ring */
#DDTStatsModal .ddt-form-area .form-control,
#DDTStatsModal .ddt-form-area .custom-select,
#DDTStatsModal .ddt-form-area textarea {
    width: 100%;
    height: 2.375rem;
    border: 1px solid #cdddec;
    border-radius: 8px;
    font-size: 0.85rem;
    color: #063c6d;
    background-color: #ffffff;
    box-shadow: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

#DDTStatsModal .ddt-form-area textarea {
    height: 2.375rem;
    min-height: 2.375rem;
    padding-top: 0.4375rem;
    resize: vertical;
}

#DDTStatsModal .ddt-form-area .form-control:focus,
#DDTStatsModal .ddt-form-area .custom-select:focus,
#DDTStatsModal .ddt-form-area textarea:focus {
    border-color: #0a5ca3;
    box-shadow: 0 0 0 3px rgba(10, 92, 163, 0.15);
    outline: none;
}

/* Section title above table */
#DDTStatsModal .ddt-section-title {
    font-size: 0.82rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.09em;
    color: #063c6d;
    border-left: 3px solid #063c6d;
    padding-left: 0.5rem;
}

/* ── Metric-card dashboard ── */
#DDTStatsModal .stats-dash {
    padding: 0.375rem 1.375rem 1.125rem;
}

#DDTStatsModal .stats-dash__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.25rem 0 0.625rem;
}

/* Toolbar: sample tabs + request */
#DDTStatsModal .stats-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.625rem;
    margin-bottom: 1rem;
}
#DDTStatsModal .stats-samples {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    padding: 0.25rem;
    background: #eef4fb;
    border-radius: 10px;
}
#DDTStatsModal .stats-sample-tab {
    border: none;
    background: transparent;
    color: #4a6880;
    font-size: 0.85rem;
    font-weight: 600;
    padding: 0.375rem 1rem;
    border-radius: 7px;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;
}
#DDTStatsModal .stats-sample-tab:hover {
    color: #063c6d;
}
#DDTStatsModal .stats-sample-tab--active {
    background: #ffffff;
    color: #063c6d;
    box-shadow: 0 1px 3px rgba(6, 60, 109, 0.15);
}
#DDTStatsModal .stats-toolbar__request {
    margin-left: auto;
}

/* ── Section cards laid out as columns ── */
#DDTStatsModal .stats-cards {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 0.875rem;
}
#DDTStatsModal .stat-col {
    flex: 1 1 16.25rem;
    min-width: 16.25rem;
    display: flex;
    flex-direction: column;
    gap: 0.875rem;
}
#DDTStatsModal .stat-card {
    background: #ffffff;
    border: 1px solid #e4ecf4;
    border-radius: 10px;
    overflow: hidden;
    box-shadow: 0 1px 4px rgba(6, 60, 109, 0.05);
}

/* Special "Summary" card — branded header, accent, larger figures */
#DDTStatsModal .stat-card--summary {
    border-color: #063c6d;
    box-shadow: 0 4px 16px rgba(6, 60, 109, 0.2);
}
#DDTStatsModal .stat-card--summary .stat-card__head {
    background: linear-gradient(135deg, #063c6d 0%, #0a5ca3 100%);
    border-bottom-color: transparent;
}
#DDTStatsModal .stat-card--summary .stat-card__icon,
#DDTStatsModal .stat-card--summary .stat-card__title {
    color: #ffffff;
}
#DDTStatsModal .stat-card--summary .stat-line {
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
}
#DDTStatsModal .stat-card--summary .stat-line__label {
    color: #063c6d;
    font-weight: 700;
}
#DDTStatsModal .stat-card--summary .stat-line__value {
    font-size: 1.1rem;
    color: #063c6d;
}

/* Full-width horizontal Summary strip */
#DDTStatsModal .stat-summary {
    width: 100%;
    margin-bottom: 1rem;
}
#DDTStatsModal .stat-summary__row {
    display: flex;
    flex-wrap: wrap;
}
#DDTStatsModal .stat-summary__item {
    flex: 1 1 12.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    padding: 0.875rem 1.25rem;
    border-right: 1px solid #eef2f7;
}
#DDTStatsModal .stat-summary__item:last-child {
    border-right: none;
}
#DDTStatsModal .stat-summary__label {
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #4a6880;
}
#DDTStatsModal .stat-summary__value {
    font-size: 1.45rem;
    font-weight: 700;
    color: #063c6d;
    font-variant-numeric: tabular-nums;
}
#DDTStatsModal .stat-card__head {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 0.875rem;
    background: #f2f6fb;
    border-bottom: 1px solid #e4ecf4;
}
#DDTStatsModal .stat-card__icon {
    font-size: 0.8rem;
    color: #0a5ca3;
}
#DDTStatsModal .stat-card__title {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #063c6d;
}
#DDTStatsModal .stat-card__body {
    padding: 2px 0;
}
#DDTStatsModal .stat-line {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.5625rem 0.875rem;
    border-bottom: 1px solid #f0f4f9;
}
#DDTStatsModal .stat-line:last-child {
    border-bottom: none;
}
#DDTStatsModal .stat-line__label {
    font-size: 0.82rem;
    font-weight: 600;
    color: #2f4a63;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
#DDTStatsModal .stat-line__value {
    flex-shrink: 0;
    font-size: 0.85rem;
    font-weight: 700;
    color: #1a6fd0;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
}

#DDTStatsModal .cell-spinner {
    width: 0.9rem;
    height: 0.9rem;
    border-width: 2px;
    color: #4A74A8;
    flex-shrink: 0;
}

#DDTStatsModal .highlightLoader {
    color: #4A74A8 !important;
}

#DDTStatsModal .stats-loading-banner {
    font-size: 0.72rem;
    font-weight: 600;
    color: #4a6880;
    gap: 0;
}

#DDTStatsModal .loading-banner-spinner {
    width: 0.75rem;
    height: 0.75rem;
    border-width: 2px;
    color: #4A74A8;
    flex-shrink: 0;
}

#DDTStatsModal .loading-banner-text {
    font-size: 0.72rem;
    color: #4a6880;
}

#DDTStatsModal .fade-enter-active,
#DDTStatsModal .fade-leave-active {
    transition: opacity 0.3s ease;
}
#DDTStatsModal .fade-enter,
#DDTStatsModal .fade-leave-to {
    opacity: 0;
}

.highlight {
    color: #0c78ff;
}

.download-button-container {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.12);
    color: #ffffff;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease, transform 0.1s ease;
}

.download-button-container:hover {
    background: rgba(255, 255, 255, 0.24);
    color: #ffffff;
}

.download-button-container:active {
    transform: translateY(1px);
}

.modal-dialog-full {
    max-width: 95vw;
    width: 80rem;
    margin: 0.5rem auto;
}

.modal-content-full {
    display: flex;
    flex-direction: column;
    color: #063C6D;
    line-height: 1.25;
    border-radius: calc(0.3rem - 1px);
}

.modal-content-full .modal-body {
    /* background: #f0f4f9; */
    border-radius: calc(0.3rem - 1px);
}

.modal-body-scroll {
    overflow-y: auto;
    max-height: calc(95vh - 7.5rem);
}

/* ── Responsive: adapt the report to smaller screens ── */
@media (max-width: 1320px) {
    .modal-dialog-full {
        width: 96vw;
    }
}

@media (max-width: 860px) {
    .modal-dialog-full {
        width: 98vw;
        max-width: 98vw;
        top: 0;
    }
    #DDTStatsModal .stats-dash {
        padding: 0.375rem 0.875rem 0.875rem;
    }
    /* Cards stack two-up then one-up as space shrinks */
    #DDTStatsModal .stat-col {
        flex: 1 1 46%;
    }
    #DDTStatsModal .ddt-form-area {
        padding: 0.875rem 1rem;
    }
    /* Form fields stack full-width */
    #DDTStatsModal .ddt-field--type,
    #DDTStatsModal .ddt-field--date {
        flex: 1 1 100%;
        width: 100%;
    }
    /* Toolbar: tabs + request stack and stretch */
    #DDTStatsModal .stats-toolbar {
        align-items: stretch;
    }
    #DDTStatsModal .stats-toolbar__request {
        margin-left: 0;
        width: 100%;
        justify-content: center;
    }
}

@media (max-width: 620px) {
    #DDTStatsModal .stat-col {
        flex: 1 1 100%;
        min-width: 0;
    }
    #DDTStatsModal .stat-summary__item {
        flex: 1 1 50%;
        border-bottom: 1px solid #eef2f7;
    }
    #DDTStatsModal .stat-summary__value {
        font-size: 1.2rem;
    }
    #DDTStatsModal .stats-samples {
        width: 100%;
        justify-content: center;
    }
}

@media (max-width: 420px) {
    #DDTStatsModal .stat-summary__item {
        flex: 1 1 100%;
        border-right: none;
    }
    #DDTStatsModal .stat-summary__item:last-child {
        border-bottom: none;
    }
}

.confirm-btn:hover {
    background-color: rgba(6, 60, 109, 0.8) !important;
}

.confirm-btn {
    font-size: 0.8rem !important;
    border: none !important;
    min-height: 1.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 0.5rem;
    background-color: #063C6D;
}

#DDTStatsModal .request-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    min-height: 2.375rem;
    padding: 0.5625rem 1.375rem;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    line-height: 1;
    color: #ffffff !important;
    border: none;
    border-radius: 999px;
    background: linear-gradient(135deg, #0a5ca3 0%, #063c6d 100%);
    box-shadow: 0 3px 10px rgba(6, 60, 109, 0.35);
    transition: transform 0.12s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

#DDTStatsModal .request-btn__icon {
    font-size: 0.8rem;
    transition: transform 0.15s ease;
}

#DDTStatsModal .request-btn:hover:not(:disabled) {
    filter: brightness(1.08);
    box-shadow: 0 5px 16px rgba(6, 60, 109, 0.45);
    transform: translateY(-1px);
}
#DDTStatsModal .request-btn:hover:not(:disabled) .request-btn__icon {
    transform: translateX(2px);
}

#DDTStatsModal .request-btn:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(6, 60, 109, 0.3);
}

#DDTStatsModal .request-btn:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    box-shadow: none;
}

/* Footer Close button (both modals) — solid blue */
#DDTStatsModal .close-btn,
#confirmSampleRequest .close-btn {
    border: 1px solid #063c6d !important;
    background: #063c6d !important;
    color: #ffffff !important;
    font-size: 0.85rem;
    font-weight: 600;
    border-radius: 8px;
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

#DDTStatsModal .close-btn:hover,
#confirmSampleRequest .close-btn:hover {
    background: #0a5ca3 !important;
    border-color: #0a5ca3 !important;
    color: #ffffff !important;
}

#DDTStatsModal .request-btn-spacer {
    height: 2.25rem;
}

#DDTStatsModal .request-btn-spacer .col-12 {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f5f8fb;
    border-bottom: 1px solid #e4ecf4;
    border-right: 1px solid #e4ecf4;
}
</style>

<script>
import axios from 'axios'
import * as XLSX from "xlsx-js-style";
import { getDateStringForFileName, styleWorksheet } from "../../../utils/utils"; 
import ptv_sample from '!!raw-loader!./../../../emails/ptv_sample.html'
import { mapGetters } from 'vuex';

// Rows shown in DDT mode. isSection rows render as divider headers in the UI but are skipped in the xlsx export.
const DDT_ROWS = [
    { label: 'Total Segments Length',   key: 'All segments length',   fallback: '0.00 km' },
    { label: 'Dropped Segments Length', key: 'Dropped segments length', fallback: '0.00 km' },

    { isSection: true, label: 'FA Attributes' },
    { label: 'FA Active Construction Length',                 key: 'FA Active Construction',                 fallback: '0.00 km' },
    { label: 'FA Compromised Road Condition Length',          key: 'FA Compromised Road Condition',          fallback: '0.00 km' },
    { label: 'FA Construction Completed Not Acquired Length', key: 'FA Construction Completed Not Acquired', fallback: '0.00 km' },

    { isSection: true, label: 'OSM Segments' },
    { label: 'OSM Segments Length',      key: 'osm_total',          fallback: '0.00 km' },
    { label: 'OSM Motorway Length',      key: 'osm_motorway',       fallback: '0.00 km' },
    { label: 'OSM Motorway Link Length', key: 'osm_motorway_link',  fallback: '0.00 km' },
    { label: 'OSM Trunk Length',         key: 'osm_trunk',          fallback: '0.00 km' },
    { label: 'OSM Trunk Link Length',    key: 'osm_trunk_link',     fallback: '0.00 km' },
    { label: 'OSM Primary Length',       key: 'osm_primary',        fallback: '0.00 km' },
    { label: 'OSM Primary Link Length',  key: 'osm_primary_link',   fallback: '0.00 km' },
    { label: 'OSM Secondary Length',     key: 'osm_secondary',      fallback: '0.00 km' },
    { label: 'OSM Secondary Link Length',key: 'osm_secondary_link', fallback: '0.00 km' },
    { label: 'OSM Tertiary Length',      key: 'osm_tertiary',       fallback: '0.00 km' },
    { label: 'OSM Tertiary Link Length', key: 'osm_tertiary_link',  fallback: '0.00 km' },
    { label: 'OSM Residential Length',   key: 'osm_residential',    fallback: '0.00 km' },
    { label: 'OSM Service Length',       key: 'osm_service',        fallback: '0.00 km' },
    { label: 'OSM Unclassified Length',  key: 'osm_unclassified',   fallback: '0.00 km' },

    { isSection: true, label: 'Road Classifications' },
    { label: 'Access Ramp Length',    key: 'RAMP',          fallback: '0.00 km' },
    { label: 'Local Length',          key: 'LOCAL',         fallback: '0.00 km' },
    { label: 'Interchange Length',    key: 'INTERCHANGE',   fallback: '0.00 km' },
    { label: 'Non Controlled Length', key: 'NON_CONTROLLED', fallback: '0.00 km' },
    { label: 'Controlled Length',     key: 'CONTROLLED',    fallback: '0.00 km' },
    { label: 'Roundabout Length',     key: 'ROUNDABOUT',    fallback: '0.00 km' },
    { label: 'Turn Maneuver Length',  key: 'TURN_MANEUVER', fallback: '0.00 km' },

    { isSection: true, label: 'Counts' },
    { label: 'Split & Merges Count', key: 'splits_merges_count', fallback: '0' },
    { label: 'Intersection Count',   key: 'intersection_count',  fallback: '0' },
];

export default {
    name: 'DT_DDTStatsModal',
    props: ['polygonWkt', 'selectedExtent', 'user', 'polygons'],
    data() {
        return {
            statsObjs: [],
            requestName: null,
            dataLoading: true,
            osm_total: null,
            requestedSample: {},
            emailRecipients: [],
            deliveryType: null,
            deliveryDate: null,
            note: null,
            activeSample: 0
        }
    },
    computed: {
        ...mapGetters(['globalUserRole', 'globalAllowedUserModules', 'globalIsUserAllowed']),

        hasPolygons() {
            return this.polygons && this.polygons.length > 0;
        },

        // Single source of truth — drives the xlsx export and is regrouped into
        // statSections for the dashboard tiles below
        visibleStatRows() {
            return DDT_ROWS;
        },

        // Groups DDT_ROWS into { label, icon, metrics[] } section cards.
        // Rows before the first section header fall under "Summary".
        statSections() {
            const icons = {
                'Summary': 'chart-bar',
                'FA Attributes': 'exclamation-triangle',
                'OSM Segments': 'map',
                'Road Classifications': 'road',
                'Counts': 'hashtag',
            };
            const sections = [];
            let current = { label: 'Summary', icon: icons['Summary'], metrics: [] };
            for (const row of DDT_ROWS) {
                if (row.isSection) {
                    if (current.metrics.length) sections.push(current);
                    current = { label: row.label, icon: icons[row.label] || 'chart-bar', metrics: [] };
                } else {
                    current.metrics.push(row);
                }
            }
            if (current.metrics.length) sections.push(current);
            return sections;
        },

        // The Summary section is rendered separately as a full-width strip on top.
        summarySection() {
            return this.statSections.find(s => s.label === 'Summary') || null;
        },

        // Arranges the remaining section cards into columns. FA Attributes and
        // Counts share one column (Counts stacked below FA); any section not placed
        // explicitly falls back to its own column. Summary is excluded (shown above).
        statColumns() {
            const byLabel = {};
            for (const s of this.statSections) byLabel[s.label] = s;

            const layout = [
                ['FA Attributes', 'Counts'],
                ['Road Classifications'],
                ['OSM Segments'],
            ];

            const used = new Set(['Summary']);
            const columns = layout
                .map(labels => {
                    const col = [];
                    labels.forEach(l => {
                        if (byLabel[l]) { col.push(byLabel[l]); used.add(l); }
                    });
                    return col;
                })
                .filter(col => col.length);

            // Safety net: append any section the layout didn't account for
            this.statSections.forEach(s => {
                if (!used.has(s.label)) columns.push([s]);
            });

            return columns;
        },

        isCellsLoading() {
            if (!this.statsObjs.length) return false;
            const dataKeys = DDT_ROWS.filter(r => !r.isSection).map(r => r.key);
            return this.statsObjs.some(obj => dataKeys.some(key => obj[key] === undefined));
        },

        showRequestBtn() {
            return this.globalIsUserAllowed('Data_Delivery_Tool_Sample_Request_Btn');
        },
    },
    methods: {
        closeStatsModal() {
            this.statsObjs = []
            this.requestedSample = {}
            this.$bvModal.hide('DDTStatsModal')
        },
        closeConfirmation() {
            this.$bvModal.hide('confirmSampleRequest')
        },
        async generateStatistics() {
            const geometries = this.polygons;
            if (!geometries.length || !this.selectedExtent) return;

            if (!sessionStorage.token) {
                this.$router.push({ name: 'Login' });
                return;
            }

            this.dataLoading = true;
            this.statsObjs = geometries.map(() => ({}));

            const fallbackMap = Object.fromEntries(
                DDT_ROWS.filter(r => !r.isSection).map(r => [r.key, r.fallback])
            );

            // Merges partial result and immediately fills fallbacks for any owned keys not returned
            const applyFallbacks = (index, partial, ownedKeys) => {
                const patched = { ...partial };
                ownedKeys.forEach(key => {
                    if (patched[key] === undefined) patched[key] = fallbackMap[key];
                });
                this.$set(this.statsObjs, index, Object.assign({}, this.statsObjs[index], patched));
            };

            const allDone = geometries.map((geom, index) => {
                const osmPromise = this.getOSMSegLength(this.selectedExtent, geom).then(partial => {
                    const osm_total = Object.entries(partial)
                        .filter(([key]) => key.startsWith('osm_') && key !== 'osm_construction')
                        .reduce((sum, [_, value]) => sum + (parseFloat(value?.toString().replace(' km', '')) || 0), 0)
                        .toFixed(2);
                    applyFallbacks(index, { ...partial, osm_total: `${osm_total} km` }, [
                        'osm_total', 'osm_motorway', 'osm_motorway_link',
                        'osm_trunk', 'osm_trunk_link', 'osm_primary', 'osm_primary_link',
                        'osm_secondary', 'osm_secondary_link', 'osm_tertiary', 'osm_tertiary_link',
                        'osm_residential', 'osm_service', 'osm_unclassified',
                    ]);
                });

                return Promise.all([
                    this.getAllSegLength(this.selectedExtent, geom)
                        .then(p => applyFallbacks(index, p, ['All segments length'])),
                    this.getDroppedSegLength(this.selectedExtent, geom)
                        .then(p => applyFallbacks(index, p, ['Dropped segments length'])),
                    this.getFASegLength(this.selectedExtent, geom)
                        .then(p => applyFallbacks(index, p, ['FA Active Construction', 'FA Compromised Road Condition', 'FA Construction Completed Not Acquired'])),
                    this.getIntersectionsSegLength(this.selectedExtent, geom)
                        .then(p => applyFallbacks(index, p, ['intersection_count'])),
                    this.getRoadTypeSegLength(this.selectedExtent, geom)
                        .then(p => applyFallbacks(index, p, ['RAMP', 'LOCAL', 'INTERCHANGE', 'NON_CONTROLLED', 'CONTROLLED', 'ROUNDABOUT', 'TURN_MANEUVER'])),
                    this.getMergesSplitsCount(this.selectedExtent, geom)
                        .then(p => applyFallbacks(index, p, ['splits_merges_count'])),
                    osmPromise,
                ]);
            });

            await Promise.all(allDone);
            this.dataLoading = false;  // reveals download button and request buttons
        },
        parseStatsResponse(content) {
            if (!content || content.errors) return {};
            const items = Array.isArray(content) ? content : [content];
            return Object.fromEntries(
                items.map(item => {
                    const raw = item.value ?? 0;
                    const value = (item.Key === 'intersection_count' || item.Key === 'splits_merges_count')
                        ? raw
                        : (raw / 1000).toFixed(2) + ' km';
                    return [item.Key, value];
                })
            );
        },
        async getAllSegLength(region, polygon) {
            try {
                const response = await axios.post(
                    `${process.env.API_LOCATION}/api/dataDeliveryTool/getPolygonSegmentLength`,
                    { region, polygon },
                    { headers: { 'x-auth-token': sessionStorage.token } }
                );
                return this.parseStatsResponse(response.data);
            } catch (error) {
                console.log(error);
                return {};
            }
        },
        async getFASegLength(region, polygon) {
            try {
                const response = await axios.post(
                    `${process.env.API_LOCATION}/api/dataDeliveryTool/getPolygonFASegmentLength`,
                    { region, polygon },
                    { headers: { 'x-auth-token': sessionStorage.token } }
                );
                return this.parseStatsResponse(response.data);
            } catch (error) {
                console.log(error);
                return {};
            }
        },
        async getDroppedSegLength(region, polygon) {
            try {
                const response = await axios.post(
                    `${process.env.API_LOCATION}/api/dataDeliveryTool/getPolygonDroppedSegmentLength`,
                    { region, polygon },
                    { headers: { 'x-auth-token': sessionStorage.token } }
                );
                return this.parseStatsResponse(response.data);
            } catch (error) {
                console.log(error);
                return {};
            }
        },
        async getIntersectionsSegLength(region, polygon) {
            try {
                const response = await axios.post(
                    `${process.env.API_LOCATION}/api/dataDeliveryTool/getPolygonIntersectionsSegmentLength`,
                    { region, polygon },
                    { headers: { 'x-auth-token': sessionStorage.token } }
                );
                return this.parseStatsResponse(response.data);
            } catch (error) {
                console.log(error);
                return {};
            }
        },
        async getOSMSegLength(region, polygon) {
            try {
                const response = await axios.post(
                    `${process.env.API_LOCATION}/api/dataDeliveryTool/getPolygonOSMSegmentLength`,
                    { region, polygon },
                    { headers: { 'x-auth-token': sessionStorage.token } }
                );
                return this.parseStatsResponse(response.data);
            } catch (error) {
                console.log(error);
                return {};
            }
        },
        async getRoadTypeSegLength(region, polygon) {
            try {
                const response = await axios.post(
                    `${process.env.API_LOCATION}/api/dataDeliveryTool/getPolygonRoadTypeSegmentLength`,
                    { region, polygon },
                    { headers: { 'x-auth-token': sessionStorage.token } }
                );
                return this.parseStatsResponse(response.data);
            } catch (error) {
                console.log(error);
                return {};
            }
        },
        async getMergesSplitsCount(region, polygon) {
            try {
                const response = await axios.post(
                    `${process.env.API_LOCATION}/api/dataDeliveryTool/getPolygonMergesSplitsCount`,
                    { region, polygon },
                    { headers: { 'x-auth-token': sessionStorage.token } }
                );
                return this.parseStatsResponse(response.data);
            } catch (error) {
                console.log(error);
                return {};
            }
        },
        reset() {
            this.requestName = null
            this.deliveryDate = null
            this.note = null
            this.dataLoading = true
            this.statsObjs = []
        },
        openConfirmation(statsObj, index) {
            this.requestedSample = statsObj;
            this.requestedSample['wkt'] = this.polygons[index];
            this.$bvModal.show('confirmSampleRequest')
        },
        submitRequest() {
          this.sendRequest()
        },
        sendRequest() {
            if (sessionStorage.token) {
                axios.post(`${process.env.API_LOCATION}/api/dataDeliveryTool/insert_requested_data`, 
                    {
                        region:                                        this.selectedExtent,
                        segment_length:                                this.requestedSample['All segments length'],
                        dropped_segment_length:                        this.requestedSample['Dropped segments length'],
                        osm_motorway_length:                           this.requestedSample['osm_motorway'],
                        osm_motorway_link_length:                      this.requestedSample['osm_motorway_link'],
                        osm_trunk_length:                              this.requestedSample['osm_trunk'],
                        osm_trunk_link_length:                         this.requestedSample['osm_trunk_link'],
                        osm_primary_length:                            this.requestedSample['osm_primary'],
                        osm_primary_link_length:                       this.requestedSample['osm_primary_link'],
                        osm_secondary_length:                          this.requestedSample['osm_secondary'],
                        osm_secondary_link_length:                     this.requestedSample['osm_secondary_link'],
                        osm_tertiary_length:                           this.requestedSample['osm_tertiary'],
                        osm_tertiary_link_length:                      this.requestedSample['osm_tertiary_link'],
                        osm_construction_length:                       this.requestedSample['osm_construction'],
                        osm_residential_length:                        this.requestedSample['osm_residential'],
                        osm_service_length:                            this.requestedSample['osm_service'],
                        osm_unclassified_length:                       this.requestedSample['osm_unclassified'],
                        osm_length:                                    this.requestedSample['osm_total'],
                        local_length:                                  this.requestedSample['LOCAL'],
                        turn_maneuver_length:                          this.requestedSample['TURN_MANEUVER'],
                        ramp_length:                                   this.requestedSample['RAMP'],
                        roundabout_length:                             this.requestedSample['ROUNDABOUT'],
                        controlled_length:                             this.requestedSample['CONTROLLED'],
                        interchange_length:                            this.requestedSample['INTERCHANGE'],
                        non_controlled_length:                         this.requestedSample['NON_CONTROLLED'],
                        merges_splits_count:                           this.requestedSample['splits_merges_count'],
                        intersection_count:                            this.requestedSample['intersection_count'],
                        fa_active_construction_length:                 this.requestedSample['FA Active Construction'],
                        fa_compromised_road_condition_length:          this.requestedSample['FA Compromised Road Condition'],
                        fa_construction_completed_not_acquired_length: this.requestedSample['FA Construction Completed Not Acquired'],
                        wkt:          this.requestedSample['wkt'],
                        user:         this.user.name,
                        deliveryType: this.deliveryType,
                        deliveryDate: this.deliveryDate,
                        note:         this.note,
                        user_id:                  this.user.id
                    },
                    {
                        headers: { 
                            'x-auth-token': sessionStorage.token 
                        }
                    }
                ).then(response => {
                    const content = response.data
                    if (content.errors) {
                        console.log('getPolygonMapData API Call Error')
                    } else {
                        this.$bvModal.hide('DDTStatsModal')
                        this.$emit('toggleSnippingTool')
                        this.$emit('removePolygons')
                        this.$bvToast.toast('Sending Email to Admin!', {
                            autoHideDelay: 1000,
                            variant: 'info',
                            solid: true,
                            title: 'In progress..',
                            toaster: 'b-toaster-bottom-right'
                        })
                        this.sendEmail()
                        this.$emit('admin-notification', {
                            region: this.selectedExtent, 
                            deliveryDate: this.deliveryDate,
                            deliveryType: this.deliveryType
                        })
                        let msg = `Your sample request has been successfully submitted and is currently under review.
                                    \nOur team will evaluate your request and update its status as it progresses.
                                    \nYou will receive a notification when there is an update.`
                                    
                        this.$emit('notification', {msg: msg, user_id: this.user.id})
                    }
                })
                .catch(function (error) {
                    console.log(error)
                    self.feedback = error.response.data.errors[0].msg
                    self.showFeedback()
                })
            } else {
                this.$router.push({ name: 'Login' })
            }
        },
        sendEmail() {
            const s   = this.requestedSample;
            const get = (key, fallback = '0.0 km') => s[key] || fallback;

            const All_segments_length                           = get('All segments length')
            const fa_active_construction_length                 = get('FA Active Construction')
            const fa_compromised_road_condition_length          = get('FA Compromised Road Condition')
            const fa_construction_completed_not_acquired_length = get('FA Construction Completed Not Acquired')
            const Dropped_segments_length                       = get('Dropped segments length')
            const osm_motorway_length                           = get('osm_motorway')
            const osm_motorway_link_length                      = get('osm_motorway_link')
            const osm_trunk_length                              = get('osm_trunk')
            const osm_trunk_link_length                         = get('osm_trunk_link')
            const OSM_Primary_Length                            = get('osm_primary')
            const osm_primary_link_length                       = get('osm_primary_link')
            const osm_secondary_length                          = get('osm_secondary')
            const osm_secondary_link_length                     = get('osm_secondary_link')
            const osm_tertiary_length                           = get('osm_tertiary')
            const osm_tertiary_link_length                      = get('osm_tertiary_link')
            const osm_residential_length                        = get('osm_residential')
            const osm_service_length                            = get('osm_service')
            const osm_unclassified_length                       = get('osm_unclassified')
            const access_ramp_length                            = get('RAMP')
            const local_length                                  = get('LOCAL')
            const interchange_length                            = get('INTERCHANGE')
            const non_controlled_length                         = get('NON_CONTROLLED')
            const controlled_length                             = get('CONTROLLED')
            const roundabout_length                             = get('ROUNDABOUT')
            const turn_maneuver_length                          = get('TURN_MANEUVER')
            const merges_splits_count                           = get('splits_merges_count', '0')
            const intersection_count                            = get('intersection_count',  '0')
            const osm_length                                    = get('osm_total')
            const delivery_type = this.deliveryType
            const delivery_date = this.deliveryDate
            const note          = this.note

            const isValidEmail = [
                All_segments_length, fa_active_construction_length,
                fa_compromised_road_condition_length, fa_construction_completed_not_acquired_length,
                Dropped_segments_length, osm_motorway_length, osm_motorway_link_length,
                osm_trunk_length, osm_trunk_link_length, OSM_Primary_Length, osm_primary_link_length,
                osm_secondary_length, osm_secondary_link_length, osm_tertiary_length,
                osm_tertiary_link_length, osm_residential_length, osm_service_length,
                osm_unclassified_length, access_ramp_length, local_length, interchange_length,
                non_controlled_length, controlled_length, roundabout_length, turn_maneuver_length,
            ].some(v => v !== '0.0 km') || merges_splits_count !== '0' || intersection_count !== '0';

            const fromEmail    = 'mmp-info@dmp-maps.com'
            const emailSubject = 'New data sample request received from MMP'
            const year         = new Date().getFullYear()

            const emailBody = ptv_sample
                .replace('{{ name }}',                                          this.user.name)
                .replace('{{ email }}',                                         this.user.email)
                .replace('{{ polygonWkt }}',                                    s['wkt'])
                .replace('{{ selectedExtent }}',                                this.selectedExtent)
                .replace('{{ all_segments_length }}',                           All_segments_length)
                .replace('{{ osm_length }}',                                    osm_length)
                .replace('{{ fa_active_construction_length }}',                 fa_active_construction_length)
                .replace('{{ fa_compromised_road_condition_length }}',          fa_compromised_road_condition_length)
                .replace('{{ fa_construction_completed_not_acquired_length }}', fa_construction_completed_not_acquired_length)
                .replace('{{ dropped_segments_length }}',                       Dropped_segments_length)
                .replace('{{ osm_motorway_length }}',                           osm_motorway_length)
                .replace('{{ osm_motorway_link_length }}',                      osm_motorway_link_length)
                .replace('{{ osm_trunk_length }}',                              osm_trunk_length)
                .replace('{{ osm_trunk_link_length }}',                         osm_trunk_link_length)
                .replace('{{ osm_primary_length }}',                            OSM_Primary_Length)
                .replace('{{ osm_primary_link_length }}',                       osm_primary_link_length)
                .replace('{{ osm_secondary_length }}',                          osm_secondary_length)
                .replace('{{ osm_secondary_link_length }}',                     osm_secondary_link_length)
                .replace('{{ osm_tertiary_length }}',                           osm_tertiary_length)
                .replace('{{ osm_tertiary_link_length }}',                      osm_tertiary_link_length)
                .replace('{{ osm_residential_length }}',                        osm_residential_length)
                .replace('{{ osm_service_length }}',                            osm_service_length)
                .replace('{{ osm_unclassified_length }}',                       osm_unclassified_length)
                .replace('{{ access_ramp_length }}',                            access_ramp_length)
                .replace('{{ local_length }}',                                  local_length)
                .replace('{{ interchange_length }}',                            interchange_length)
                .replace('{{ non_controlled_length }}',                         non_controlled_length)
                .replace('{{ controlled_length }}',                             controlled_length)
                .replace('{{ roundabout_length }}',                             roundabout_length)
                .replace('{{ turn_maneuver_length }}',                          turn_maneuver_length)
                .replace('{{ merges_splits_count }}',                           merges_splits_count)
                .replace('{{ intersection_count }}',                            intersection_count)
                .replace('{{ year }}',                                          year)
                .replace('{{ delivery_type }}',                                 delivery_type)
                .replace('{{ delivery_date }}',                                 delivery_date)
                .replace('{{ note }}',                                          note)

            const now      = new Date();
            const mm       = String(now.getMonth() + 1).padStart(2, '0');
            const dd       = String(now.getDate()).padStart(2, '0');
            const yyyy     = now.getFullYear();
            const hh       = String(now.getHours()).padStart(2, '0');
            const min      = String(now.getMinutes()).padStart(2, '0');
            const fileName = `data_sample_${mm}_${dd}_${yyyy}_${hh}_${min}`   
            const endpoint = 'sendEmailWithWKT';

            if (sessionStorage.token) {
                if (isValidEmail) {
                    axios.post(
                        `${process.env.API_LOCATION}/api/email/${endpoint}`,
                        {
                            fromEmail:    fromEmail,
                            toEmail:      this.emailRecipients,
                            emailSubject: emailSubject,
                            emailBody:    emailBody,
                            wkt:          s['wkt'],
                            fileName:     fileName
                        },
                        { headers: { 'x-auth-token': sessionStorage.token } }
                    )
                    .then(response => {
                        const content = response.data
                        if (content.errors) {
                            console.log('sendEmail API Call Error')
                        } else {
                            if (content.some(obj => obj.status !== 'bounced' || obj.status !== 'rejected')) {
                                this.$bvToast.toast('Data Sample request Email sent.', {
                                    autoHideDelay: 5000,
                                    variant: 'success',
                                    solid: true,
                                    title: 'Email Sent',
                                    toaster: 'b-toaster-bottom-right'
                                })
                                this.$bvModal.hide('DDTStatsModal')
                            }
                        }
                    })
                    .catch(function (error) {
                        console.log(error)
                        self.feedback = error.response.data.errors[0].msg
                        self.showFeedback()
                    })
                } else {
                    this.$bvToast.toast('There is no data to generate a sample.', {
                        autoHideDelay: 7000,
                        variant: 'danger',
                        solid: true,
                        title: 'Email Not Sent',
                        toaster: 'b-toaster-bottom-right'
                    })
                    this.$bvModal.hide('DDTStatsModal')
                }
            } else {
                this.$router.push({ name: 'Login' })
            }
        },
        getEmailRecipients() {
            let module_name = ''

            switch (this.selectedExtent) {
                case 'Japan':
                    module_name = 'ptv_sample_japan'
                    break;
                default:
                    module_name = 'ptv_sample'
                    break;
            }

            if (sessionStorage.token) {
                axios.get(`${process.env.API_LOCATION}/api/email/getRecipients`, {
                    headers: { 'x-auth-token': sessionStorage.token },
                    params:  { module: module_name }
                })
                .then(response => {
                    this.emailRecipients = response.data[0].recipients;
                })
                .catch(function (error) {
                    console.log(error)
                })
            } else {
                console.log('No User Token')
            }
        },
        downloadReports() {
            let wb = XLSX.utils.book_new()
            const deepCopyStats = JSON.parse(JSON.stringify(this.statsObjs))
            let ordered = new Map(Object.entries(deepCopyStats))

            ordered.forEach((statsObj, index) => {
                let ws_data = new Map()

                ws_data.set('Polygon Wkt', this.polygons[index])
                ws_data.set('Region',       this.selectedExtent)
                ws_data.set('deliveryType', this.deliveryType || ' ')
                ws_data.set('deliveryDate', this.deliveryDate || ' ')
                ws_data.set('note',         this.note         || ' ')
                ws_data.set('', '')
                ws_data.set('Attribute', 'Value')

                // visibleStatRows keeps the xlsx in sync with exactly what the UI shows; skip section headers
                this.visibleStatRows.filter(r => !r.isSection).forEach(row => {
                    ws_data.set(row.label, statsObj[row.key] || row.fallback)
                })

                const jsonStr = JSON.parse(JSON.stringify([...ws_data]))
                let ws = XLSX.utils.aoa_to_sheet(jsonStr)
                styleWorksheet(ws, { leftAlignNumbers: true, boldCols: [0], boldRows: [6] });
                XLSX.utils.book_append_sheet(wb, ws, 'Request_' + (parseInt(index) + 1))
            })

            const timestampstring = getDateStringForFileName()
            XLSX.writeFile(wb, `Statistics_Report_${timestampstring}.xlsx`)
        },
    },
    mounted() {
        this.getEmailRecipients()
    },
    watch: {
        selectedExtent() {
            this.getEmailRecipients();
        }
    }
}
</script>